import { WidgetVisibilityContext } from 'components/Widgets/WidgetVisibilityContext';
import { useContext, useState } from 'react';
import { DOPPLER_CONFIG } from '../../config/dopplerRadar.config.js';
import useDopplerMapLocation from '../../hooks/useDopplerMapLocation.js';
import useDopplerPlayback from '../../hooks/useDopplerPlayback.js';
import DopplerLoadingOverlay from './components/DopplerLoadingOverlay/DopplerLoadingOverlay.jsx';
import DopplerMapControls from './components/DopplerMapControls/DopplerMapControls.jsx';
import DopplerRadarLayers from './components/DopplerRadarLayers/DopplerRadarLayers.jsx';

const DOPPLER_MAX_BOUNDS = DOPPLER_CONFIG.maxBounds;
const DEFAULT_DOPPLER_ZOOM = DOPPLER_CONFIG.defaultZoom;
const MIN_DOPPLER_ZOOM = DOPPLER_CONFIG.minZoom;

export default function DopplerMapContent({
  compact = false,
  scrollZoom = true,
  useSinglePanelCompactControls = false,
}) {
  const { mapBounds, mapCenter } = useDopplerMapLocation();
  const { isVisible: isWidgetVisible = true } = useContext(WidgetVisibilityContext);
  const [bufferState, setBufferState] = useState({
    isPlaybackReady: false,
    showLoadingOverlay: true,
    showViewLoadingOverlay: false,
    viewLoadingReason: null,
    startupReadyFrameCount: 0,
    startupTargetFrameCount: 0,
  });

  const {
    baseTime,
    frames,
    displayedFrameIndex,
    requestedFrameIndex,
    isPlaying,
    playbackSpeed,
    setIsPlaying,
    setPlaybackSpeed,
    requestFrame,
    handleDisplayedFrameChange,
    setIsTransitioning,
  } = useDopplerPlayback({
    isPlaybackReady: bufferState.isPlaybackReady,
    isActive: isWidgetVisible,
  });
  const mapWrapperClassName = `position-relative h-100 w-100 rounded-2 overflow-hidden${
    compact ? ' p-2' : ''
  }`;
  const mapProps = {
    mapCenter,
    bounds: mapBounds,
    zoom: DEFAULT_DOPPLER_ZOOM,
    minZoom: MIN_DOPPLER_ZOOM,
    maxZoom: DOPPLER_CONFIG.maxZoom,
    maxBounds: DOPPLER_MAX_BOUNDS,
    maxBoundsViscosity: DOPPLER_CONFIG.maxBoundsViscosity,
    disableWorldWrap: DOPPLER_CONFIG.disableWorldWrap,
    scrollZoom,
  };
  const playbackProps = {
    frames,
    displayedFrameIndex,
    isPlaying,
    isPlaybackReady: bufferState.isPlaybackReady,
    playbackSpeed,
    onTogglePlayback: () => setIsPlaying((isCurrentlyPlaying) => !isCurrentlyPlaying),
    onSpeedChange: setPlaybackSpeed,
    onScrub: requestFrame,
  };

  return (
    <div className={mapWrapperClassName}>
      <DopplerMapControls
        compact={compact}
        mapProps={mapProps}
        playbackProps={playbackProps}
        useSinglePanelCompactControls={useSinglePanelCompactControls}
      >
        <DopplerRadarLayers
          baseTime={baseTime}
          frames={frames}
          displayedFrameIndex={displayedFrameIndex}
          requestedFrameIndex={requestedFrameIndex}
          onDisplayedFrameChange={handleDisplayedFrameChange}
          onTransitionStateChange={setIsTransitioning}
          onBufferStateChange={setBufferState}
        />
      </DopplerMapControls>

      {(bufferState.showLoadingOverlay || bufferState.showViewLoadingOverlay) && (
        <DopplerLoadingOverlay
          showViewLoadingOverlay={bufferState.showViewLoadingOverlay}
          viewLoadingReason={bufferState.viewLoadingReason}
          startupReadyFrameCount={bufferState.startupReadyFrameCount}
          startupTargetFrameCount={bufferState.startupTargetFrameCount}
        />
      )}
    </div>
  );
}
